import React, { Component, createContext } from "react";

const StorefrontContext = createContext();

class StorefrontProvider extends Component {
  state = {
    cart: null,
    products: [],
    collections: [],
    isCartOpen: false,
  };

  // Storefront API GraphQL client
  storefrontClient = {
    domain: "rcdepron.myshopify.com",
    storefrontAccessToken: "251729e85b2d4d5e111f8fd3a5f83e84",
    apiVersion: "2024-04",

    async query(query, variables = {}) {
      const response = await fetch(
        `https://${this.domain}/api/${this.apiVersion}/graphql.json`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": this.storefrontAccessToken,
          },
          body: JSON.stringify({
            query,
            variables,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.json();
    },
  };

  componentDidMount() {
    this.createCart();
  }

  createCart = async () => {
    const CREATE_CART_MUTATION = `
      mutation cartCreate($input: CartInput) {
        cartCreate(input: $input) {
          cart {
            id
            checkoutUrl
            lines(first: 100) {
              edges {
                node {
                  id
                  quantity
                  merchandise {
                    ... on ProductVariant {
                      id
                      title
                      price {
                        amount
                        currencyCode
                      }
                      product {
                        id
                        title
                        handle
                        images(first: 1) {
                          edges {
                            node {
                              url
                              altText
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
            cost {
              totalAmount {
                amount
                currencyCode
              }
              subtotalAmount {
                amount
                currencyCode
              }
              totalTaxAmount {
                amount
                currencyCode
              }
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    try {
      const response = await this.storefrontClient.query(CREATE_CART_MUTATION, {
        input: {},
      });

      if (response.data?.cartCreate?.cart) {
        const cart = response.data.cartCreate.cart;
        localStorage.setItem("cartId", cart.id);
        await this.setState({ cart });
      } else {
        console.error(
          "Error creating cart:",
          response.data?.cartCreate?.userErrors
        );
      }
    } catch (error) {
      console.error("Error creating cart:", error);
    }
  };

  fetchCart = async (cartId) => {
    const GET_CART_QUERY = `
      query getCart($cartId: ID!) {
        cart(id: $cartId) {
          id
          checkoutUrl
          lines(first: 100) {
            edges {
              node {
                id
                quantity
                merchandise {
                  ... on ProductVariant {
                    id
                    title
                    price {
                      amount
                      currencyCode
                    }
                    product {
                      id
                      title
                      handle
                      images(first: 1) {
                        edges {
                          node {
                            url
                            altText
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
          cost {
            totalAmount {
              amount
              currencyCode
            }
            subtotalAmount {
              amount
              currencyCode
            }
            totalTaxAmount {
              amount
              currencyCode
            }
          }
        }
      }
    `;

    try {
      const response = await this.storefrontClient.query(GET_CART_QUERY, {
        cartId,
      });

      if (response.data?.cart) {
        await this.setState({ cart: response.data.cart });
      }
    } catch (error) {
      console.error("Error fetching cart:", error);
    }
  };

  addItemToCart = async (variantId, quantity) => {
    const ADD_TO_CART_MUTATION = `
      mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
        cartLinesAdd(cartId: $cartId, lines: $lines) {
          cart {
            id
            checkoutUrl
            lines(first: 100) {
              edges {
                node {
                  id
                  quantity
                  merchandise {
                    ... on ProductVariant {
                      id
                      title
                      price {
                        amount
                        currencyCode
                      }
                      product {
                        id
                        title
                        handle
                        images(first: 1) {
                          edges {
                            node {
                              url
                              altText
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
            cost {
              totalAmount {
                amount
                currencyCode
              }
              subtotalAmount {
                amount
                currencyCode
              }
              totalTaxAmount {
                amount
                currencyCode
              }
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    if (!this.state.cart?.id) {
      console.error("No cart available");
      return;
    }

    try {
      const response = await this.storefrontClient.query(ADD_TO_CART_MUTATION, {
        cartId: this.state.cart.id,
        lines: [
          {
            merchandiseId: variantId,
            quantity: parseInt(quantity, 10),
          },
        ],
      });

      if (response.data?.cartLinesAdd?.cart) {
        console.log(
          "StorefrontContext: Item added to cart",
          response.data.cartLinesAdd.cart
        );
        await this.setState({ cart: response.data.cartLinesAdd.cart });
        this.openCart();
      } else {
        console.error(
          "Error adding item to cart:",
          response.data?.cartLinesAdd?.userErrors
        );
      }
    } catch (error) {
      console.error("Error adding item to cart:", error);
    }
  };

  removeItemFromCart = async (lineId) => {
    const REMOVE_FROM_CART_MUTATION = `
      mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
        cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
          cart {
            id
            checkoutUrl
            lines(first: 100) {
              edges {
                node {
                  id
                  quantity
                  merchandise {
                    ... on ProductVariant {
                      id
                      title
                      price {
                        amount
                        currencyCode
                      }
                      product {
                        id
                        title
                        handle
                        images(first: 1) {
                          edges {
                            node {
                              url
                              altText
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
            cost {
              totalAmount {
                amount
                currencyCode
              }
              subtotalAmount {
                amount
                currencyCode
              }
              totalTaxAmount {
                amount
                currencyCode
              }
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    if (!this.state.cart?.id) {
      console.error("No cart available");
      return;
    }

    try {
      const response = await this.storefrontClient.query(
        REMOVE_FROM_CART_MUTATION,
        {
          cartId: this.state.cart.id,
          lineIds: [lineId],
        }
      );

      if (response.data?.cartLinesRemove?.cart) {
        await this.setState({ cart: response.data.cartLinesRemove.cart });
      } else {
        console.error(
          "Error removing item from cart:",
          response.data?.cartLinesRemove?.userErrors
        );
      }
    } catch (error) {
      console.error("Error removing item from cart:", error);
    }
  };

  updateItemQuantity = async (lineId, quantity) => {
    const UPDATE_CART_MUTATION = `
      mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
        cartLinesUpdate(cartId: $cartId, lines: $lines) {
          cart {
            id
            checkoutUrl
            lines(first: 100) {
              edges {
                node {
                  id
                  quantity
                  merchandise {
                    ... on ProductVariant {
                      id
                      title
                      price {
                        amount
                        currencyCode
                      }
                      product {
                        id
                        title
                        handle
                        images(first: 1) {
                          edges {
                            node {
                              url
                              altText
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
            cost {
              totalAmount {
                amount
                currencyCode
              }
              subtotalAmount {
                amount
                currencyCode
              }
              totalTaxAmount {
                amount
                currencyCode
              }
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    if (!this.state.cart?.id) {
      console.error("No cart available");
      return;
    }

    try {
      const response = await this.storefrontClient.query(UPDATE_CART_MUTATION, {
        cartId: this.state.cart.id,
        lines: [
          {
            id: lineId,
            quantity: parseInt(quantity, 10),
          },
        ],
      });

      if (response.data?.cartLinesUpdate?.cart) {
        await this.setState({ cart: response.data.cartLinesUpdate.cart });
      } else {
        console.error(
          "Error updating cart:",
          response.data?.cartLinesUpdate?.userErrors
        );
      }
    } catch (error) {
      console.error("Error updating cart:", error);
    }
  };

  fetchAllProducts = async () => {
    const GET_PRODUCTS_QUERY = `
      query getProducts($first: Int!) {
        products(first: $first) {
          edges {
            node {
              id
              title
              handle
              description
              images(first: 5) {
                edges {
                  node {
                    url
                    altText
                  }
                }
              }
              variants(first: 10) {
                edges {
                  node {
                    id
                    title
                    price {
                      amount
                      currencyCode
                    }
                    availableForSale
                  }
                }
              }
            }
          }
        }
      }
    `;

    try {
      const response = await this.storefrontClient.query(GET_PRODUCTS_QUERY, {
        first: 100,
      });

      if (response.data?.products?.edges) {
        const products = response.data.products.edges.map((edge) => edge.node);
        await this.setState({ products });
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  fetchAllCollections = async () => {
    const GET_COLLECTIONS_QUERY = `
      query getCollections($first: Int!) {
        collections(first: $first) {
          edges {
            node {
              id
              title
              handle
              description
              products(first: 10) {
                edges {
                  node {
                    id
                    title
                    handle
                    images(first: 1) {
                      edges {
                        node {
                          url
                          altText
                        }
                      }
                    }
                    variants(first: 1) {
                      edges {
                        node {
                          id
                          price {
                            amount
                            currencyCode
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    try {
      const response = await this.storefrontClient.query(
        GET_COLLECTIONS_QUERY,
        {
          first: 100,
        }
      );

      if (response.data?.collections?.edges) {
        const collections = response.data.collections.edges.map(
          (edge) => edge.node
        );
        await this.setState({ collections });
      }
    } catch (error) {
      console.error("Error fetching collections:", error);
    }
  };

  fetchProductWithId = async (id) => {
    const GET_PRODUCT_QUERY = `
      query getProduct($id: ID!) {
        product(id: $id) {
          id
          title
          handle
          description
          images(first: 10) {
            edges {
              node {
                url
                altText
              }
            }
          }
          variants(first: 100) {
            edges {
              node {
                id
                title
                price {
                  amount
                  currencyCode
                }
                availableForSale
              }
            }
          }
        }
      }
    `;

    try {
      const response = await this.storefrontClient.query(GET_PRODUCT_QUERY, {
        id,
      });

      if (response.data?.product) {
        return response.data.product;
      }
    } catch (error) {
      console.error("Error fetching product:", error);
    }
  };

  closeCart = () => {
    this.setState({ isCartOpen: false });
  };

  openCart = () => {
    console.log("StorefrontContext: Opening cart");
    this.setState({ isCartOpen: true });
  };

  render() {
    return (
      <StorefrontContext.Provider
        value={{
          ...this.state,
          fetchAllProducts: this.fetchAllProducts,
          fetchAllCollections: this.fetchAllCollections,
          fetchProductWithId: this.fetchProductWithId,
          closeCart: this.closeCart,
          openCart: this.openCart,
          addItemToCart: this.addItemToCart,
          removeItemFromCart: this.removeItemFromCart,
          updateItemQuantity: this.updateItemQuantity,
        }}
      >
        {this.props.children}
      </StorefrontContext.Provider>
    );
  }
}

const StorefrontConsumer = StorefrontContext.Consumer;

export { StorefrontConsumer, StorefrontContext };
export default StorefrontProvider;
